import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import Footer from '../components/Footer';
import LoadingPage from '../components/LoadingPage';
import StripePaymentForm from '../components/StripePaymentForm';
import { landingPageService } from '../services/landingPageService';
import { CreatorLandingPage } from '../types/database';
import { identifyCreator } from '../utils/mixpanel';
import { supabase } from '../lib/supabase';
import stripePromise from '../lib/stripe';
import { StripeService } from '../services/stripeService';

// Fallback favicon path
const FALLBACK_FAVICON = '/favicon.png';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  country: string;
  selectedPlan: string;
  termsAccepted: boolean;
}

interface FormErrors {
  email: string;
  password: string;
  general: string;
  payment: string;
}

// Plan data structure for future Stripe integration
const SUBSCRIPTION_PLANS = {
  monthly: {
    id: 'price_monthly',
    name: 'Monthly subscription',
    price: 10,
    interval: 'month',
    displayPrice: '$10',
    displayInterval: '/month'
  },
  yearly: {
    id: 'price_yearly',
    name: 'Yearly subscription',
    price: 99,
    interval: 'year',
    displayPrice: '$99',
    displayInterval: '/year',
    originalPrice: 120
  }
};

// Comprehensive list of all countries
const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'BN', name: 'Brunei Darussalam' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Democratic Republic of the Congo' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: 'Côte d\'Ivoire' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FK', name: 'Falkland Islands (Malvinas)' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HM', name: 'Heard Island and McDonald Islands' },
  { code: 'VA', name: 'Holy See (Vatican City State)' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran, Islamic Republic of' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'Democratic People\'s Republic of Korea' },
  { code: 'KR', name: 'Republic of Korea' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Lao People\'s Democratic Republic' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao' },
  { code: 'MK', name: 'Macedonia, Republic of' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NU', name: 'Niue' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russian Federation' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'SH', name: 'Saint Helena, Ascension and Tristan da Cunha' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'MF', name: 'Saint Martin (French part)' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'GS', name: 'South Georgia and the South Sandwich Islands' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SZ', name: 'Swaziland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syrian Arab Republic' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania, United Republic of' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VE', name: 'Venezuela, Bolivarian Republic of' },
  { code: 'VN', name: 'Viet Nam' },
  { code: 'VG', name: 'Virgin Islands, British' },
  { code: 'VI', name: 'Virgin Islands, U.S.' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
].sort((a, b) => a.name.localeCompare(b.name));

const SignUp: React.FC = () => {
  const { creatorId } = useParams<{ creatorId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [landingPageData, setLandingPageData] = useState<CreatorLandingPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: '',
    selectedPlan: 'yearly',
    termsAccepted: false
  });

  const [errors, setErrors] = useState<FormErrors>({
    email: '',
    password: '',
    general: '',
    payment: ''
  });

  // Get the referrer path from state or default to home
  const referrerPath = location.state?.from || '/';

  // Password strength check
  const isPasswordStrong = formData.password.length >= 8;
  const passwordStrengthText = formData.password.length > 0 
    ? (isPasswordStrong ? 'Excellent password!' : `${8 - formData.password.length} more characters needed`)
    : '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const landingPage = await landingPageService.getLandingPageByCreatorId(creatorId || '');
        
        if (!landingPage) {
          throw new Error('Landing page not found');
        }

        setLandingPageData(landingPage);

        // Set the accent color if we have landing page data
        if (landingPage.accent_color) {
          const accentColor = landingPage.accent_color.startsWith('#') 
            ? landingPage.accent_color 
            : `#${landingPage.accent_color}`;
          document.documentElement.style.setProperty('--accent-color', accentColor);
        }

        // Set the favicon
        const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (faviconLink) {
          faviconLink.href = landingPage.favicon || FALLBACK_FAVICON;
        }

        // Identify the creator in Mixpanel
        identifyCreator(String(landingPage.creator_id), landingPage.name);

      } catch (err) {
        console.error('Error fetching landing page data:', err);
        setError('Failed to load page data');
      } finally {
        setIsLoading(false);
      }
    };

    if (creatorId) {
      fetchData();
    }
  }, [creatorId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handlePlanSelection = (plan: 'monthly' | 'yearly') => {
    setFormData(prev => ({ ...prev, selectedPlan: plan }));
  };

  const handlePaymentSubmit = async (paymentMethod: any) => {
    // Reset payment errors
    setErrors(prev => ({ ...prev, payment: '' }));
    
    // Basic validation before payment
    const newErrors: FormErrors = {
      email: '',
      password: '',
      general: '',
      payment: ''
    };

    // Email validation
    if (!formData.email.includes('@')) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.password = 'Passwords do not match';
    }

    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Check for required fields
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      newErrors.general = 'First name and last name are required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.country) {
      newErrors.general = 'Country is required';
    }

    if (!formData.termsAccepted) {
      newErrors.general = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    
    // If there are validation errors, don't proceed with payment
    if (newErrors.email || newErrors.password || newErrors.general) {
      return;
    }

    // Start submission process
    setIsSubmitting(true);

    try {
      // Create subscription using Stripe with the payment method
      const plans = StripeService.getAvailablePlans();
      const selectedPlan = plans[formData.selectedPlan as keyof typeof plans];
      
      const subscriptionResult = await StripeService.createSubscription({
        priceId: selectedPlan.id,
        customerEmail: formData.email,
        customerName: `${formData.firstName} ${formData.lastName}`,
        creatorId: creatorId || '',
        userId: '', // We'll update this after user creation
        paymentMethodId: paymentMethod.id
      });

      // Now confirm the payment using the client_secret and payment method
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);
      
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }
      
      const { error: confirmError } = await (stripe as any).confirmCardPayment(
        subscriptionResult.client_secret,
        {
          payment_method: paymentMethod.id,
        }
      );

      if (confirmError) {
        console.error('Payment confirmation failed:', confirmError);
        setErrors(prev => ({
          ...prev,
          payment: confirmError.message || 'Payment failed. Please check your card details and try again.'
        }));
        setIsSubmitting(false);
        return;
      }

      // If we get here, the payment was confirmed successfully
      // Now create the user account
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            selected_plan: formData.selectedPlan,
            plan_details: SUBSCRIPTION_PLANS[formData.selectedPlan as keyof typeof SUBSCRIPTION_PLANS],
            country: formData.country,
            stripe_subscription_id: subscriptionResult.subscriptionId
          },
          emailRedirectTo: `${window.location.origin}/${creatorId}/success`
        }
      });

      if (error) {
        console.error('Signup error:', error);
        
        // Handle specific error cases
        if (error.message.includes('already registered')) {
          setErrors(prev => ({
            ...prev,
            email: 'An account with this email already exists'
          }));
        } else if (error.message.includes('password')) {
          setErrors(prev => ({
            ...prev,
            password: 'Password does not meet requirements'
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            general: error.message || 'Failed to create account'
          }));
        }
        setIsSubmitting(false);
        return;
      }

      if (data.user) {
        // Create EndUserDetails record
        const { error: endUserError } = await supabase
          .from('EndUserDetails')
          .insert({
            MembershipType: formData.selectedPlan,
            StripeID: subscriptionResult.subscriptionId, // Use the subscription ID
            AuthID: data.user.id,
            CreatorID: creatorId,
            FirstName: formData.firstName,
            LastName: formData.lastName,
            email: formData.email,
            country: formData.country,
            legal_accepted: formData.termsAccepted
          });

        if (endUserError) {
          console.error('EndUserDetails creation error:', endUserError);
          setErrors(prev => ({ 
            ...prev, 
            general: 'Account creation failed. Please try again or contact support@appetiteapp.io' 
          }));
          setIsSubmitting(false);
          return;
        }

        // Success - redirect to success page
        navigate(`/${creatorId}/success`, { 
          state: { 
            email: formData.email,
            from: location.pathname 
          } 
        });
      }

    } catch (paymentError) {
      console.error('Payment error:', paymentError);
      
      // Check if it's a specific payment error
      const errorMessage = paymentError instanceof Error ? paymentError.message : String(paymentError);
      
      if (errorMessage.includes('client_secret')) {
        setErrors(prev => ({
          ...prev,
          payment: 'Payment setup failed. Please try again or contact support.'
        }));
      } else if (errorMessage.includes('card')) {
        setErrors(prev => ({
          ...prev,
          payment: 'Card payment failed. Please check your card details and try again.'
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          payment: 'Payment failed. Please check your card details and try again.'
        }));
      }
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setError(null);
    setErrors({ email: '', password: '', general: '', payment: '' });

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/google-callback?creator_id=${creatorId}`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error('Google OAuth error:', error);
        setError('Failed to start Google sign-up. Please try again.');
        return;
      }

      // The user will be redirected to Google OAuth
      // We'll handle the callback in a separate component
      console.log('Google OAuth initiated:', data);

    } catch (err) {
      console.error('Unexpected error during Google sign-up:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingPage message="Loading sign up page..." />;
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontFamily: 'Gabarito, sans-serif',
        color: 'red'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div className='signup-container'>
      <div className='signup-header'>
        <div className='signup-header-content'>
        <div className='signup-logo'>
          <button onClick={() => navigate(referrerPath)}>
          {landingPageData?.logo && (
            <img
              src={landingPageData.logo} 
              alt={landingPageData.name} 
              style={{ maxHeight: '40px', width: 'auto' }}
            />
          )}</button>
        </div>
        <button className='signup-cancel paragraphs medium color charcoal' onClick={() => navigate(referrerPath)}>Cancel</button>
      </div>
        </div>
      <div className='signup-content'>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className='signup-details'>
            <p className='subtitles large color black'>Your details</p>
            <div className='row-container'>
              <label>
                <span className='labels large color black'>First name <span className='required'>*</span></span>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder='Your first name'
                  className='paragraphs medium color black'
                />
              </label>
              <label>
                <span className='labels large color black'>Last name <span className='required'>*</span></span>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder='Your last name'
                  className='paragraphs medium color black'
                />
              </label>
            </div>
              <label>
                <span className='labels large color black'>Email address <span className='required'>*</span></span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder='Your email address'
                  className='paragraphs medium color black'
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </label>
              <label>
                <span className='labels large color black'>Password <span className='required'>*</span></span>
                <div className='password-input-container'>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    placeholder='Your password'
                    className='paragraphs medium color black'
                  />
                  <button
                    type="button"
                    className='password-toggle'
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {formData.password.length > 0 && (
                  <div className={`password-strength ${isPasswordStrong ? 'strong' : 'weak'}`}>
                    <div className='strength-indicator'>
                      <div className={`strength-bar ${isPasswordStrong ? 'filled' : ''}`}></div>
                    </div>
                    <span className='strength-text'>{passwordStrengthText}</span>
                  </div>
                )}
              </label>
      
              <label>
                <span className='labels large color black'>Confirm password <span className='required'>*</span></span>
                <div className='password-input-container'>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    placeholder='Confirm your password'
                    className='paragraphs medium color black'
                  />
                  <button
                    type="button"
                    className='password-toggle'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
                {formData.confirmPassword.length > 0 && (
                  <div className={`password-strength ${formData.password === formData.confirmPassword ? 'strong' : 'weak'}`}>                    <span className='strength-text'>
                      {formData.password === formData.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  </div>
                )}
                {errors.password && <span className="error-message">{errors.password}</span>}
              </label>
            
              {/* Display general errors */}
              {errors.general && (
                <div className="error-message general-error">
                  {errors.general}
                </div>
              )}

              {/* Temporarily hidden - will restore later
              <div className='or-divider'>
                <span className='line'>&nbsp;</span>
                <span className='paragraphs medium color charcoal'>or</span>
                <span className='line'>&nbsp;</span>
              </div>
              */}
              <div className='signup-social' style={{ display: 'none' }}>
                {/* Temporarily hidden - will restore later
                <button 
                  type="button"
                  className='btn sso-google'
                  onClick={handleGoogleSignUp}
                  disabled={isGoogleLoading}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_200_279)">
                      <path d="M23.7141 12.2245C23.7141 11.2413 23.6343 10.5238 23.4616 9.77966H12.2336V14.2176H18.8242C18.6914 15.3205 17.9739 16.9815 16.3793 18.0976L16.357 18.2462L19.907 20.9964L20.153 21.0209C22.4118 18.9347 23.7141 15.8653 23.7141 12.2245Z" fill="#4285F4"/>
                      <path d="M12.2335 23.9176C15.4623 23.9176 18.173 22.8545 20.1529 21.0209L16.3792 18.0976C15.3693 18.8018 14.014 19.2934 12.2335 19.2934C9.07109 19.2934 6.38703 17.2074 5.43023 14.324L5.28999 14.3359L1.59857 17.1927L1.55029 17.3269C3.51683 21.2334 7.55625 23.9176 12.2335 23.9176Z" fill="#34A853"/>
                      <path d="M5.43041 14.324C5.17796 13.5799 5.03185 12.7826 5.03185 11.9588C5.03185 11.1349 5.17796 10.3377 5.41713 9.5936L5.41044 9.43513L1.67277 6.53241L1.55047 6.59058C0.739971 8.21168 0.274902 10.0321 0.274902 11.9588C0.274902 13.8855 0.739971 15.7058 1.55047 17.3269L5.43041 14.324Z" fill="#FBBC05"/>
                      <path d="M12.2335 4.62403C14.4791 4.62403 15.9938 5.59402 16.8575 6.40461L20.2326 3.10928C18.1598 1.1826 15.4623 0 12.2335 0C7.55625 0 3.51683 2.68406 1.55029 6.59056L5.41695 9.59359C6.38703 6.7102 9.07109 4.62403 12.2335 4.62403Z" fill="#EB4335"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_200_279">
                        <rect width="24" height="24" fill="white"/>
                      </clipPath>
                    </defs>
                  </svg>
                  {isGoogleLoading ? 'Signing up...' : 'Sign up with Google'}
                </button>
                */}
                <button className='btn sso-facebook' style={{ display: 'none' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2.03998C6.5 2.03998 2 6.52998 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.84998C10.44 7.33998 11.93 5.95998 14.22 5.95998C15.31 5.95998 16.45 6.14998 16.45 6.14998V8.61998H15.19C13.95 8.61998 13.56 9.38998 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96C15.9164 21.5878 18.0622 20.3855 19.6099 18.57C21.1576 16.7546 22.0054 14.4456 22 12.06C22 6.52998 17.5 2.03998 12 2.03998Z" fill="#061ED3"/>
                </svg>
                 Sign up with Facebook</button>
                <button className='btn sso-apple' style={{ display: 'none' }}><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_200_34)">
                    <path d="M21.7186 18.7039C21.3557 19.5424 20.926 20.3142 20.4282 21.0239C19.7497 21.9913 19.1942 22.6609 18.766 23.0327C18.1023 23.6431 17.3912 23.9557 16.6297 23.9735C16.0831 23.9735 15.4238 23.8179 14.6564 23.5024C13.8865 23.1883 13.1789 23.0327 12.532 23.0327C11.8534 23.0327 11.1257 23.1883 10.3474 23.5024C9.5678 23.8179 8.9398 23.9824 8.45966 23.9987C7.72943 24.0298 7.00158 23.7083 6.27506 23.0327C5.81136 22.6283 5.23136 21.935 4.53654 20.9527C3.79106 19.9039 3.17817 18.6876 2.69803 17.3009C2.1838 15.8031 1.92603 14.3527 1.92603 12.9486C1.92603 11.3401 2.27358 9.95289 2.96973 8.79037C3.51684 7.85659 4.24469 7.12 5.15565 6.57926C6.06662 6.03852 7.05091 5.76296 8.11091 5.74533C8.69091 5.74533 9.45151 5.92474 10.3967 6.27733C11.3392 6.63111 11.9444 6.81052 12.2097 6.81052C12.4081 6.81052 13.0804 6.60074 14.2201 6.18252C15.2979 5.79467 16.2075 5.63407 16.9527 5.69733C18.972 5.8603 20.489 6.6563 21.4979 8.09037C19.692 9.18459 18.7986 10.7172 18.8164 12.6833C18.8327 14.2147 19.3882 15.489 20.4801 16.5009C20.9749 16.9705 21.5275 17.3335 22.1423 17.5913C22.009 17.9779 21.8682 18.3483 21.7186 18.7039ZM17.0875 0.480148C17.0875 1.68046 16.649 2.80119 15.7749 3.83852C14.7201 5.0717 13.4442 5.7843 12.0607 5.67185C12.0431 5.52785 12.0328 5.3763 12.0328 5.21704C12.0328 4.06474 12.5345 2.83156 13.4253 1.82326C13.87 1.31274 14.4357 0.888252 15.1216 0.54963C15.806 0.216059 16.4534 0.0315852 17.0623 0C17.0801 0.160459 17.0875 0.320948 17.0875 0.480148Z" fill="white"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_200_34">
                      <rect width="24" height="24" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                 Sign up with Apple</button>
              </div>
          </div>
          <div className='signup-payment'>
            <p className='subtitles large color black'>Choose your plan</p>
            <div className='payment-plans-container'>
              <div 
                className={`signup-payment-plan ${formData.selectedPlan === 'monthly' ? 'selected' : ''}`}
                onClick={() => handlePlanSelection('monthly')}
                style={{ cursor: 'pointer' }}
              >
                <div className='plan-selected-icon'>
                  {formData.selectedPlan === 'monthly' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11.5" fill="#E55936" stroke="#E55936"/>
                      <circle cx="12" cy="12" r="4" fill="white"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11.5" fill="white" stroke="#CFCFCF"/>
                    </svg>
                  )}
                </div>
                <div className='plan-name'>
                  <span className='subtitles large color black'>Monthly subscription</span>
                </div>
                <div className='plan-price'>
                  <span className='price-hero titles small color black'>$10<span className='labels large color charcoal'>/month</span></span>
                </div>
              </div>
              <div 
                className={`signup-payment-plan ${formData.selectedPlan === 'yearly' ? 'selected' : ''}`}
                onClick={() => handlePlanSelection('yearly')}
                style={{ cursor: 'pointer' }}
              >
                <div className='plan-selected-icon'>
                  {formData.selectedPlan === 'yearly' ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11.5" fill="#E55936" stroke="#E55936"/>
                      <circle cx="12" cy="12" r="4" fill="white"/>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11.5" fill="white" stroke="#CFCFCF"/>
                    </svg>
                  )}
                </div>
                <div className='plan-name'>
                  <span className='subtitles large color black'>Yearly subscription</span>
                  <span className='labels small color black saving-highlight'>Save 17.5% compared to monthly</span>
                </div>
                <div className='plan-price'>
                  <span className='price-hero titles small color black'>$99<span className='labels large color charcoal'>/year</span></span>
                  <span className='price-strikethrough labels medium color charcoal'>$120/year</span>
                </div>
              </div>
            </div>
            {/* Commenting out for now - will restore later
              <div className='payment-option-google-pay'>Pay with google</div>
              <div className='payment-option-google-pay'>Pay with Apple Pay</div>
              <div className='payment-option-credit-card'>
                <div className='or-divider'>
                <span className='line'>&nbsp;</span>
                  <span className='paragraphs medium color charcoal divider-content'>or pay with credit card</span>
                  <span className='line'>&nbsp;</span>
                </div>
              </div>*/}
              
              {/* STRIPE PAYMENT FORM */}
              <div className='payment-option-credit-card'>
                <div className='or-divider'>
                  <span className='line'>&nbsp;</span>
                  <span className='paragraphs medium color charcoal divider-content'>Pay with credit card</span>
                  <span className='line'>&nbsp;</span>
                </div>
              </div>
              
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  onSubmit={handlePaymentSubmit}
                  isProcessing={isSubmitting}
                  error={errors.payment}
                  onErrorClear={() => setErrors(prev => ({ ...prev, payment: '' }))}
                  amount={formData.selectedPlan === 'monthly' ? 10 : 99}
                  currency="usd"
                />
              </Elements>
              
              <label>
                <span className='labels large color black'>Country <span className='required'>*</span></span>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className='paragraphs medium color black'
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map(country => (
                    <option key={country.code} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </label>
              
              <p className='paragraphs medium color charcoal'>By providing your card information, you allow Appetite App Pty Ltd to charge your card for future payments on behalf of Chelsea Goodwin in accordance with their terms.</p>
              <label className='checkbox-container'>
                <input 
                  type="checkbox" 
                  name="termsAccepted" 
                  id="terms" 
                  checked={formData.termsAccepted}
                  onChange={handleCheckboxChange}
                />
                <span className='paragraphs medium color black'>I agree to the <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
              </label>
              <button 
                type="submit" 
                className="btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating Account...' : 'Sign up to our community'}
              </button>
          </div>
        </form>
      </div>
      <Footer creatorId={creatorId} />
    </div>
  );
};

export default SignUp; 